import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  inject,
  signal,
} from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { Router } from "@angular/router";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { finalize } from "rxjs";
import { DestroyRef } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { UserLoginRequest } from "src/app/models/user-login.model";
import { AlertHelper } from "src/app/helper/alert.helper";
import { AuthService } from "src/app/services/auth.service";
import { RegisterComponent } from "../form-register/register.component";

@Component({
  selector: "app-login",
  imports: [ReactiveFormsModule],
  templateUrl: "./login.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private auth = inject(AuthService);
  private alert = inject(AlertHelper);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private modalService = inject(NgbModal);

  @Input() modal!: NgbModalRef;
  @Input() afterLoginUrl?: string;

  loginForm!: FormGroup;

  hide = signal(true);
  notActive = signal(false);
  deleted = signal(false);
  loading = signal(false);
  isForgotPasswordMode = signal(false);
  forgotEmailControl = new FormControl("", [
    Validators.required,
    Validators.email,
  ]);

  ngOnInit() {
    this.loginForm = this.formBuilder.group({
      mailAddress: ["", [Validators.required, Validators.email]],
      password: ["", [Validators.required]],
    });
  }

  submitForgotPassword() {
    if (this.forgotEmailControl.invalid) {
      this.forgotEmailControl.markAsTouched();
      return;
    }

    this.loading.set(true);
    const email = this.forgotEmailControl.value!;

    this.auth
      .forgotPassword(email)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (res) => {
          if (res.status === 200) {
            this.alert.success(
              "Terkirim",
              "Silakan cek email Anda untuk instruksi reset password.",
            );
            this.backToLogin();
          } else {
            this.alert.error(
              "Gagal",
              res.message || "Gagal memproses permintaan.",
            );
          }
        },
        error: (err) => {
          this.alert.error(
            "Gagal",
            err.error?.message || "Gagal memproses permintaan.",
          );
        },
      });
  }

  get form() {
    return this.loginForm.controls;
  }

  hidePassword() {
    this.hide.update((v) => !v);
  }

  login() {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.loading.set(true);
    this.notActive.set(false);
    this.deleted.set(false);

    const payload: UserLoginRequest = this.loginForm.getRawValue();

    this.auth
      .loginAsync(payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({
        next: (res) => {
          switch (res.status) {
            case 200:
              // Wait for user info before navigation to prevent race condition
              this.auth
                .getUserInfo()
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                  next: () => {
                    this.alert.success("Berhasil", "Login berhasil!");
                    this.modal.close();
                    if (this.afterLoginUrl) {
                      this.router.navigateByUrl(this.afterLoginUrl);
                    } else {
                      this.router.navigateByUrl("/home");
                    }
                  },
                  error: (err) => {
                    console.error("Failed to load user info:", err);
                    this.alert.warning(
                      "",
                      "Role Anda tidak bisa digunakan pada website ini, silahkan login pada website bank sertifikat.",
                    );
                    this.modal.close();
                    this.router.navigateByUrl("/home");
                  },
                });
              break;
            case 403:
              this.notActive.set(true);
              break;
            case 410:
              this.deleted.set(true);
              break;
            default:
              this.alert.error("Gagal", res.message ?? "Gagal login.");
          }
        },
        error: (err) => {
          this.alert.error("Error", err.error?.message || "Terjadi kesalahan.");
        },
      });
  }

  forgotPassword(email: string) {
    if (!email || this.form["mailAddress"].invalid) {
      this.alert.error("Info", "Masukkan email yang valid.");
      return;
    }
    this.auth.forgotPassword(email).subscribe({
      next: () => this.alert.success("Terkirim", "Cek email reset password."),
      error: (err) => this.alert.error("Gagal", err.error?.message),
    });
  }

  switchToForgotPassword() {
    const currentEmail = this.loginForm.get("mailAddress")?.value;
    if (currentEmail) {
      this.forgotEmailControl.setValue(currentEmail);
    }
    this.isForgotPasswordMode.set(true);
  }

  backToLogin() {
    this.isForgotPasswordMode.set(false);
    this.notActive.set(false);
    this.deleted.set(false);
  }

  openRegisterModal() {
    this.modal.close();
    const modalRef = this.modalService.open(RegisterComponent, {
      centered: true,
    });
    modalRef.componentInstance.modal = modalRef;
  }
}
